import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { dashboardService, googleIntegrationsService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import AssistantPreferences from '../ai/AssistantPreferences';
import {
  ArrowRightOnRectangleIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  LinkIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, updateProfile, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [saving, setSaving] = useState(false);

  const { data: dashboardData } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardService.getDashboard(),
  });

  const { data: integrationStatus } = useQuery({
    queryKey: ['google-integration'],
    queryFn: () => googleIntegrationsService.getStatus(),
    retry: false,
  });

  const stats = dashboardData?.stats || {};
  const connected = Boolean((integrationStatus?.calendar_access ?? integrationStatus?.google_calendar_connected) && !integrationStatus?.reconnect_required);

  const saveProfile = async () => {
    setSaving(true);
    const result = await updateProfile({ full_name: fullName.trim() });
    if (result.success) {
      toast.success('Profile updated');
      setIsEditing(false);
    } else {
      toast.error(result.error || 'Could not update profile');
    }
    setSaving(false);
  };

  const signOut = () => {
    logout();
    navigate('/login');
  };

  const initial = (user?.full_name || user?.username || 'U').charAt(0).toUpperCase();

  return (
    <div className="app-page">
      <div className="app-page-inner max-w-5xl">
        <header className="page-header">
          <div>
            <div className="eyebrow">
              <UserCircleIcon className="h-4 w-4" />
              Account and workspace
            </div>
            <h1 className="page-title">Profile</h1>
            <p className="page-description">
              Manage your identity, connected services, and the account that owns your planning context.
            </p>
          </div>
          <button onClick={signOut} className="secondary-button">
            <ArrowRightOnRectangleIcon className="h-4 w-4" />
            Sign out
          </button>
        </header>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-4">
            <section className="surface-panel p-5 sm:p-6">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-center gap-4">
                  <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-[#246b52] text-xl font-bold text-white">
                    {initial}
                  </span>
                  <div className="min-w-0">
                    <h2 className="truncate text-xl font-bold">{user?.full_name || user?.username || 'Timelyzer user'}</h2>
                    <p className="mt-1 text-sm text-[#68746d]">@{user?.username}</p>
                    <p className="mt-1 text-xs text-[#8a948e]">
                      Member since {user?.created_at ? format(new Date(user.created_at), 'MMMM yyyy') : 'recently'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setFullName(user?.full_name || '');
                    setIsEditing((current) => !current);
                  }}
                  className="secondary-button"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                  {isEditing ? 'Cancel' : 'Edit profile'}
                </button>
              </div>

              {isEditing ? (
                <div className="mt-6 border-t border-[#e3e8e4] pt-5">
                  <label htmlFor="profile-name" className="field-label">Full name</label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input id="profile-name" value={fullName} onChange={(event) => setFullName(event.target.value)} className="field-input flex-1" />
                    <button onClick={saveProfile} disabled={saving || !fullName.trim()} className="primary-button disabled:opacity-50">
                      {saving ? 'Saving' : 'Save changes'}
                    </button>
                  </div>
                </div>
              ) : (
                <dl className="mt-6 grid gap-px overflow-hidden rounded-md border border-[#dfe5e0] bg-[#dfe5e0] sm:grid-cols-2">
                  <div className="bg-[#fbfcfb] p-4">
                    <dt className="text-[11px] font-bold uppercase text-[#7c8780]">Email address</dt>
                    <dd className="mt-2 break-all text-sm font-semibold">{user?.email || 'Not set'}</dd>
                  </div>
                  <div className="bg-[#fbfcfb] p-4">
                    <dt className="text-[11px] font-bold uppercase text-[#7c8780]">Account status</dt>
                    <dd className="mt-2 flex items-center gap-2 text-sm font-semibold">
                      <CheckCircleIcon className="h-4 w-4 text-[#2f7c5b]" />
                      Active
                    </dd>
                  </div>
                </dl>
              )}
            </section>

            <AssistantPreferences />

            <section className="surface-panel overflow-hidden">
              <div className="flex items-center justify-between border-b border-[#e2e7e3] p-5">
                <div>
                  <h2 className="section-heading">Workspace activity</h2>
                  <p className="mt-1 text-xs text-[#7a857e]">A quick view of the context stored in Timelyzer.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 divide-x divide-y divide-[#e8ece9] sm:grid-cols-4 sm:divide-y-0">
                {[
                  ['Events', stats.total_events || 0, CalendarDaysIcon],
                  ['Notes', stats.total_notes || 0, DocumentTextIcon],
                  ['AI context', stats.ai_enhanced_events || 0, SparklesIcon],
                  ['Suggestions', stats.total_recommendations || 0, ShieldCheckIcon],
                ].map(([label, value, Icon]) => (
                  <div key={label} className="p-4">
                    <Icon className="h-5 w-5 text-[#68746d]" />
                    <p className="mt-4 text-2xl font-bold">{value}</p>
                    <p className="mt-1 text-xs font-semibold text-[#7b867f]">{label}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside className="space-y-4">
            <section className="surface-panel p-5">
              <div className="flex items-start justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[#e2edf6] text-[#32699a]">
                  <CalendarDaysIcon className="h-5 w-5" />
                </span>
                <span className={`status-pill ${connected ? 'status-pill-success' : 'status-pill-warning'}`}>
                  {!integrationStatus ? 'Status unknown' : connected ? 'Authorization saved' : 'Setup needed'}
                </span>
              </div>
              <h2 className="mt-5 text-base font-bold">Google Calendar</h2>
              <p className="mt-2 text-sm leading-6 text-[#6c7770]">
                {connected
                  ? 'Calendar permission is saved. Review source refresh history and sync results in Connections.'
                  : 'Open Connections to check setup and source access. Saved authorization does not verify live availability.'}
              </p>
              <button onClick={() => navigate('/integrations')} className="secondary-button mt-5 w-full">
                <LinkIcon className="h-4 w-4" />
                Manage integration
              </button>
            </section>

            <section className="surface-panel border-[#cfe0d6] bg-[#f4f8f5] p-5">
              <ShieldCheckIcon className="h-5 w-5 text-[#246b52]" />
              <h2 className="mt-3 text-sm font-bold">Your planning context</h2>
              <p className="mt-2 text-xs leading-5 text-[#68746d]">
                Event and note context is scoped to this account. Sign out when using a shared device.
              </p>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
